/**
 * Ctrl+K command palette — the logic half (SellerMate W0.2).
 *
 * components/CommandPalette.tsx is the view; everything it decides lives here
 * as pure functions so the keyboard contract is testable without a DOM:
 *
 *   - which PAGES match (from the sidebar's ITEM_CATALOG — one list of screens,
 *     so a page renamed in the sidebar is renamed here too);
 *   - how the server's record sections (GET /api/search, see
 *     backend/api/command_search.py) flatten into one navigable list;
 *   - where each record opens (the same drill URLs the list pages already
 *     understand — the incoming-filter convention in useIncomingFilter.ts);
 *   - what a key press does (Up / Down / Enter / Ctrl+Enter / Esc).
 *
 * Matching pages uses the one search grammar (lib/searchQuery.ts) so a query
 * means the same thing here as in every other search box.
 */
import { matchesParsed, parseSearch } from './searchQuery'
import { adsHref, adsOpenHref } from '../pages/ads/routing'
import type { AiContext } from './aiPanel'

// ── server payload ──────────────────────────────────────────────────────────

export type CommandSearchKind =
  | 'campaign' | 'ad_group' | 'keyword' | 'target' | 'product' | 'rule' | 'po'

export type CommandSearchItem = {
  kind: CommandSearchKind
  id: string
  label: string
  sublabel?: string
  campaign_id?: string | null
  campaign_name?: string | null
  ad_group_id?: string | null
  ad_group_name?: string | null
  asin?: string | null
  rule_id?: string | null
  po_number?: string | null
}

export type CommandSearchSection = {
  kind: CommandSearchKind
  label: string
  items: CommandSearchItem[]
  truncated: boolean
  error?: string
}

export type CommandSearchResult = {
  q: string
  sections: CommandSearchSection[]
  too_short: boolean
}

/** The server answers nothing below this length (backend MIN_QUERY_LEN). */
export const MIN_QUERY_LEN = 2
/** Pages shown at most, so records are never pushed off the screen. */
export const MAX_PAGES = 6

// ── pages ───────────────────────────────────────────────────────────────────

export type PageEntry = { id: string; label: string; path: string }

/** Pages whose label or path matches `q`. A blank query lists none — the
 *  palette opens empty and fills as you type. */
export function matchPages(entries: PageEntry[], q: string, max = MAX_PAGES): PageEntry[] {
  const parsed = parseSearch(q)
  if (parsed.length === 0) return []
  return entries
    .filter(e => matchesParsed(`${e.label}\x1f${e.path}`.toLowerCase(), parsed))
    .slice(0, max)
}

// ── links ───────────────────────────────────────────────────────────────────

const enc = encodeURIComponent

/** Where a record opens. Every URL is one a list page already reads. */
export function entityHref(item: CommandSearchItem): string {
  switch (item.kind) {
    case 'campaign': {
      const id = item.campaign_id || item.id
      const name = item.campaign_name || item.label
      // The workspace fetches the row with include_inactive itself, so a
      // paused or archived campaign still opens.
      return adsOpenHref('campaign', { campaignId: id }, { campaignName: name || undefined })
    }
    case 'ad_group': {
      const id = item.ad_group_id || item.id
      const name = item.ad_group_name || item.label
      return adsOpenHref('ad_group', { adGroupId: id }, { adGroupName: name || undefined })
    }
    case 'keyword':
      return adsHref('targets', item.ad_group_id ? { adGroupId: item.ad_group_id } : {}, undefined, { kind: 'keywords' })
    case 'target':
      return adsHref('targets', item.ad_group_id ? { adGroupId: item.ad_group_id } : {})
    case 'product':
      return `/products/${enc(item.asin || item.id)}`
    case 'rule':
      return `/rules/${enc(item.rule_id || item.id)}/edit`
    case 'po':
      return `/inventory/orders?po=${enc(item.po_number || item.id)}`
  }
}

/** The AI Assistant page reads `?ask=` and fills it in as a new question; the operator presses Send. */
export const ASK_AI_PARAM = 'ask'

export function askAiHref(q: string): string {
  return `/ai-assistant?${ASK_AI_PARAM}=${enc(q.trim())}`
}

/** What "Ask AI" hands the header's AI side panel (openAiPanel): the typed
 *  text as the question, on the Ask tab. The palette uses this instead of
 *  navigating to the AI Assistant page (A12). */
export function askPanelOpts(q: string): { prompt: string; tab: 'ask'; context: AiContext } {
  // context: {} drops the page/selection left by an earlier header Ask AI, so
  // a general question is not framed by the previous page.
  return { prompt: q.trim(), tab: 'ask', context: {} }
}

// ── the flattened, navigable list ───────────────────────────────────────────

export type PaletteRow =
  | { type: 'page'; key: string; label: string; sublabel: string; href: string }
  | { type: 'record'; key: string; label: string; sublabel: string; href: string; kind: CommandSearchKind }
  | { type: 'ask'; key: string; label: string; sublabel: string; href: string }

export type PaletteGroup = { key: string; label: string; rows: PaletteRow[]; note?: string }

/** Groups in display order: Pages, then each record section that has rows (or
 *  an error to show), then "Ask AI" whenever there is a query. */
export function buildGroups(
  q: string,
  pages: PageEntry[],
  result: CommandSearchResult | undefined,
): PaletteGroup[] {
  const text = q.trim()
  const groups: PaletteGroup[] = []
  if (pages.length) {
    groups.push({
      key: 'pages',
      label: 'Pages',
      rows: pages.map(p => ({
        type: 'page', key: `page:${p.id}`, label: p.label, sublabel: p.path, href: p.path,
      })),
    })
  }
  for (const s of result?.sections ?? []) {
    if (!s.items.length && !s.error) continue
    groups.push({
      key: s.kind,
      label: s.label,
      note: s.error ?? (s.truncated ? 'More match — keep typing to narrow it down.' : undefined),
      rows: s.items.map(it => ({
        type: 'record', kind: s.kind, key: `${s.kind}:${it.id}`,
        label: it.label, sublabel: it.sublabel ?? '', href: entityHref(it),
      })),
    })
  }
  if (text) {
    groups.push({
      key: 'ask',
      label: 'Ask AI',
      rows: [{
        type: 'ask', key: 'ask', label: `Ask AI: ${text}`,
        sublabel: 'Ctrl+Enter from anywhere in the list', href: askAiHref(text),
      }],
    })
  }
  return groups
}

/**
 * The record results that answer what is typed NOW, else undefined. Records
 * from the previous query - still in hand while the next request is debounced
 * or in flight - were listed, highlighted, and one Enter away from opening a
 * record the reader no longer asked for (type "slime", then "cleaver", press
 * Enter: a slime campaign opens). The server echoes the text it answered
 * (`q`, trimmed), so a result counts only when that is the typed text.
 */
export function currentResult(
  result: CommandSearchResult | undefined,
  typed: string,
): CommandSearchResult | undefined {
  return result && result.q === typed.trim() ? result : undefined
}

export function flattenRows(groups: PaletteGroup[]): PaletteRow[] {
  return groups.flatMap(g => g.rows)
}

// ── keyboard ────────────────────────────────────────────────────────────────

export type PaletteKey = { key: string; ctrlKey?: boolean; metaKey?: boolean }

export type PaletteAction =
  | { type: 'move'; index: number }
  | { type: 'open'; index: number }
  | { type: 'ask' }
  | { type: 'close' }
  | null

/** What one key press does, given the highlighted row and the row count.
 *  Up/Down wrap; Enter opens the highlighted row; Ctrl/Cmd+Enter always asks
 *  the AI (even with a record highlighted); Esc closes. Anything else is
 *  typing and belongs to the input (null). */
export function paletteKeyAction(e: PaletteKey, index: number, count: number): PaletteAction {
  switch (e.key) {
    case 'Escape':
      return { type: 'close' }
    case 'ArrowDown':
      if (count === 0) return null
      return { type: 'move', index: index < 0 ? 0 : (index + 1) % count }
    case 'ArrowUp':
      if (count === 0) return null
      return { type: 'move', index: index <= 0 ? count - 1 : index - 1 }
    case 'Enter':
      if (e.ctrlKey || e.metaKey) return { type: 'ask' }
      if (count === 0) return null
      return { type: 'open', index: index < 0 || index >= count ? 0 : index }
    default:
      return null
  }
}

/** Ctrl+K on Windows, Cmd+K on a Mac. Not with Shift or Alt held, so other
 *  chords stay free. */
export function isCommandPaletteHotkey(
  e: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; shiftKey?: boolean },
): boolean {
  return (e.key === 'k' || e.key === 'K')
    && !!(e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey
}

/** Keep the highlight on a real row as results arrive and leave. */
export function clampIndex(index: number, count: number): number {
  if (count === 0) return -1
  if (index < 0) return 0
  return Math.min(index, count - 1)
}
